// import node module libraries
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LayoutItem } from "@/types";
import { v4 as uuid } from "uuid";

// Initial layout is empty

interface gridProps {
  layout: LayoutItem[];
  counter: number;
  title: string;
  description: string;
  dashboardInsights: string[];
  isDashboardLoading: boolean;
}

const initialState: gridProps = {
  layout: [],
  counter: 0,
  title: "Enter Dashboard Name",
  description:
    "Describe the purpose of this dashboard or key metrics displayed",
  dashboardInsights: [],
  isDashboardLoading: false,
};

const gridSlice = createSlice({
  name: "grid",
  initialState,
  reducers: {
    addItem: (
      state,
      action: PayloadAction<{
        id: string;
        title: string;
        desc?: string;
        type?: any;
        content: any;
      }>
    ) => {
      // Define constants for the layout
      const totalColumns = 3; // Number of columns per row
      const itemWidth = 4; // Width of each item
      const itemHeight = 8; // Height of each item

      // Determine the number of items in the layout and find the current row
      const items = state.layout;
      const numItems = items.length;

      // Calculate the row index and column index for the new item
      const rowIndex = Math.floor(numItems / totalColumns);
      const colIndex = numItems % totalColumns;

      // Calculate x and y positions based on the row and column index
      const xPosition = colIndex * itemWidth;
      const yPosition = rowIndex * itemHeight;

      const { id, title, desc, type, content } = action.payload;

      // Add the new item with the calculated x and y position
      state.layout.push({
        i: id,
        x: xPosition,
        y: yPosition,
        w: itemWidth,
        h: itemHeight,
        data: {
          id,
          title,
          desc: action.payload.desc || "",
          type,
          content,
        },
      });

      state.counter += 1;
    },
    removeItem: (state, action: PayloadAction<string>) => {
      state.layout = state.layout.filter((item) => item.i !== action.payload);
    },
    setLayout: (state, action: PayloadAction<LayoutItem[]>) => {
      // Update layout while keeping existing data for known items
      const updatedLayout = action.payload.map((newItem) => {
        const existingItem = state.layout.find((item) => item.i === newItem.i);
        return existingItem
          ? { ...newItem, data: existingItem.data } // Retain existing data
          : newItem; // New item, no data to preserve
      });
      state.layout = updatedLayout;
    },
    updateTitle: (
      state,
      action: PayloadAction<{ id: string; title: string }>
    ) => {
      const { id, title } = action.payload;
      const item = state.layout.find((item) => item.i === id);
      if (item) {
        item.data.title = title;
      }
    },
    updateWidgetContent: (
      state,
      action: PayloadAction<{ id: string; type: string; content: any }>
    ) => {
      const { id, type, content } = action.payload;
      const item = state.layout.find((item) => item.i === id);
      if (item) {
        item.data.type = type;
        item.data.content = { ...item.data.content, ...content };
      }
    },
    copyItem: (state, action: PayloadAction<string>) => {
      const newItemId = action.payload;
      const existingItem = state.layout.find((item) => item.i === newItemId);
      if (existingItem) {
        const newId = uuid();

        // Define constants for the layout
        const totalColumns = 3; // Number of columns per row
        const itemWidth = 4; // Width of each item
        const itemHeight = 8; // Height of each item

        // Calculate the new position for the copied item
        const numItems = state.layout.length;
        const rowIndex = Math.floor(numItems / totalColumns);
        const colIndex = numItems % totalColumns;
        const xPosition = colIndex * itemWidth;
        const yPosition = rowIndex * itemHeight;

        // Add the new item with the calculated x and y position
        state.layout.push({
          i: newId,
          x: xPosition,
          y: yPosition,
          w: existingItem.w,
          h: existingItem.h,
          data: {
            ...existingItem.data,
            id: newId,
            title: existingItem.data.title + "-Copy",
          },
        });
      }
    },
    addDashboardTitle: (
      state,
      action: PayloadAction<{ title: string; description: string }>
    ) => {
      const { title, description } = action.payload;
      state.title = title;
      state.description = description;
    },
    addDashboardInsights: (state, action: PayloadAction<string[]>) => {
      state.isDashboardLoading = true;
      state.dashboardInsights = action.payload;
    },
    dashboardLoading: (state) => {
      state.isDashboardLoading = true;
    },
    dashboardLoadingStop: (state) => {
      state.isDashboardLoading = false;
    },
  },
});

export const {
  addItem,
  removeItem,
  setLayout,
  updateTitle,
  updateWidgetContent,
  copyItem,
  addDashboardTitle,
  addDashboardInsights,
  dashboardLoading,
  dashboardLoadingStop,
} = gridSlice.actions;
export default gridSlice.reducer;
